import { Outlet, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Database, Cable, Cpu, FileCode2, LayoutGrid, ShieldAlert, Wrench, LogOut, ChevronLeft, FileDigit, FileCheck2, FileSearch, Fingerprint, LifeBuoy, Unlock, Lock, Table2, Download, PanelLeftClose, PanelLeftOpen, Zap, Image as ImageIcon, History,
  LayoutDashboard, User, ChevronDown, Settings as SettingsIcon, Bike,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
// Library update banner hidden by user request (2026-10-19) — update lives in Settings.
// import BasemapUpdateBanner from "./BasemapUpdateBanner";
import { useI18n } from "../context/I18nContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import LicenseBanner from "./LicenseBanner";
import UpdateBanner, { CheckUpdateButton } from "./UpdateBanner";

const BASE_NAV = [
  { name: "ECU Database", i18n: "nav.database", path: "/database", icon: Database, badge: "Grid" },
  { name: "Converter", i18n: "nav.binConverter", path: "/bin-converter", icon: FileDigit, badge: "Convert" },
  { name: "Checksum", i18n: "nav.checksum", path: "/honda-titan-checksum", icon: FileCheck2, badge: "Auto" },
  { name: "ECU Pinout", i18n: "nav.pinout", path: "/pinout", icon: Cable, badge: "4 Brands", featureId: "pinout" },
  { name: "XDF / ADX Unlock", i18n: "nav.xdfAdx", path: "/xdf-adx-toolkit", icon: FileSearch, badge: "Decrypt", featureId: "xdf-adx" },
  { name: "IMMO Service", i18n: "nav.immo", path: "/immo-calculator", icon: Fingerprint, badge: "EEPROM", featureId: "immo" },
  { name: "Recovery Generator", i18n: "nav.recovery", path: "/recovery-generator", icon: LifeBuoy, badge: "R8C", featureId: "recovery" },
  { name: "Map Editor", i18n: "nav.mapEditor", path: "/map-editor", icon: Table2, badge: "XDF", featureId: "map-editor" },
  { name: "Honda Flash Tools", i18n: "nav.hondaFlashtools", path: "/honda-flashtools", icon: Bike, badge: "Honda", featureId: "map-editor" },
  { name: "MCU Read/Write", path: "/mcu-read-write", icon: Cpu, badge: "J-Link · Renesas", featureId: "map-editor" },
  { name: "Seed Key View", i18n: "nav.seedKey", path: "/shindengen-seedkey", icon: Unlock, badge: "Shindengen", featureId: "seed-key", adminOnly: true },
];

// Offline desktop (.exe) build only exposes fully client-side tools.
const OFFLINE_ONLY = new Set([
  "/bin-converter", "/honda-titan-checksum", "/xdf-adx-toolkit",
  "/immo-calculator", "/recovery-generator", "/map-editor", "/shindengen-seedkey", "/honda-flashtools", "/yamaha-flashtools",
]);
const IS_DESKTOP = typeof window !== "undefined" && window.tiroDesktop === true;
const IS_EXE = typeof window !== "undefined" && window.tiroDesktopApp === true;
const IS_WIN_EXE = typeof window !== "undefined" && window.__TRMD_NATIVE === "windows";
const EXE_HIDDEN = new Set(["/file-service"]);
const DASHBOARD_NAV = { name: "Dashboard", path: "/workspace", icon: LayoutDashboard, badge: "Home" };
// Desktop sidebar: curated labels/order (mirrors the ECU Workspace template).
const EXE_NAV = [
  DASHBOARD_NAV,
  { name: "Honda Flash Tools", path: "/honda-flashtools", icon: Bike, featureId: "map-editor" },
  { name: "MCU Read/Write", path: "/mcu-read-write", icon: Cpu, featureId: "mcu-read-write" },
  { name: "Map Editor", path: "/map-editor", icon: LayoutGrid, featureId: "map-editor" },
  { name: "XDF / ADX Unlock", path: "/xdf-adx-toolkit", icon: FileSearch, featureId: "xdf-adx" },
  { name: "ECU Pinout", path: "/pinout", icon: Cable, featureId: "pinout" },
  { name: "IMMO Service", path: "/immo-calculator", icon: Fingerprint, featureId: "immo" },
  { name: "Recovery Generator", path: "/recovery-generator", icon: LifeBuoy, featureId: "recovery" },
  { name: "ECU Database", path: "/database", icon: Database },
  { name: "Converter", path: "/bin-converter", icon: FileDigit },
  { name: "Checksum", path: "/honda-titan-checksum", icon: FileCheck2 },
  { name: "Settings", path: "/settings", icon: SettingsIcon },
];
const NAV = (IS_EXE
  ? EXE_NAV.map((n) => ({ ...(BASE_NAV.find((b) => b.path === n.path) || {}), ...n, i18n: undefined }))
  : IS_DESKTOP ? BASE_NAV.filter((n) => OFFLINE_ONLY.has(n.path)) : BASE_NAV)
  .filter((n) => !(IS_EXE && n.path === "/yamaha-flashtools"));

export default function Layout() {
  const { user, isAdmin, logout, settings, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  // Leaving a full-screen tool (Map Editor etc.) → sidebar comes back automatically.
  useEffect(() => { if (!location.pathname.startsWith("/map-editor")) setCollapsed(false); }, [location.pathname]);
  const [pendingCount, setPendingCount] = useState(0);

  // Auto-hide the sidebar when a BIN/XDF is opened in Map Editor (more room for the maps).
  useEffect(() => {
    const collapse = () => { setCollapsed(true); setOpen(false); };
    window.addEventListener("tt-collapse-sidebar", collapse);
    return () => window.removeEventListener("tt-collapse-sidebar", collapse);
  }, []);

  useEffect(() => {
    if (!isAdmin) { setPendingCount(0); return; }
    let alive = true;
    const fetchCount = async () => {
      try { const r = await api.get("/auth/users/pending-count"); if (alive) setPendingCount(r.data?.count || 0); } catch (e) {}
    };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [isAdmin]);

  // Auto Sync: on EXE open, check if the offline map DB is outdated and nudge the user.

  // Unsaved-work guard (set by pages like Map Editor via Outlet context).
  const guardRef = useRef(null);
  const registerGuard = (fn) => { guardRef.current = fn; };
  const guardConfirm = () => {
    const lvl = guardRef.current && guardRef.current();
    if (lvl === "hard") {
      toast.warning(t("guard.flashing"), { duration: 4000 });
      return false;
    }
    if (lvl === "soft" || lvl === true) {
      return window.confirm(t("guard.leave"));
    }
    return true;
  };
  const onNavClick = (e) => { if (!guardConfirm()) { e.preventDefault(); return; } setOpen(false); };

  // A feature shows green "Unlock" only when the account is admin, or approved (status
  // active) with a valid license AND the specific feature has been granted by the admin.
  // Otherwise it shows a grey "Locked" pill.
  const accountApproved = isAdmin || (!!user && user.status === "active" && !user.license_expired);
  const isUnlocked = (item) => {
    if (isAdmin) return true;
    if (!accountApproved) return false;
    if (!item.featureId) return true; // free tool — open once the account is approved
    if (item.featureId === "mcu-read-write") return Array.isArray(user.features) && user.features.includes(item.featureId);
    return !Array.isArray(user.features) || user.features.includes(item.featureId);
  };

  const [userMenu, setUserMenu] = useState(false);

  // EXE: nothing is reachable without an account — always start on the login screen.
  if (IS_EXE && !authLoading && !user) return <Navigate to="/login" replace />;

  return (
    <div className={`text-slate-200 flex ${IS_EXE ? "h-screen overflow-hidden flex-col bg-[#0d0d0d] exe-theme" : "min-h-screen bg-[#07090e]"}`}>
      {IS_EXE && (
        <header data-testid="exe-topbar" className="tt-dark h-16 shrink-0 bg-[#111111] border-b border-[#262626] flex items-center px-3 sm:px-5 gap-2 sm:gap-4 relative z-50">
          <img src={settings?.site_logo || "/trmd-logo.webp"} alt="TRMD" className="h-9 sm:h-11 w-auto max-w-[132px] sm:max-w-[210px] object-contain cursor-pointer transition-opacity hover:opacity-80" onClick={() => { if (guardConfirm()) navigate("/workspace"); }} data-testid="exe-logo" />
          <div className="ml-auto min-w-0 flex items-center gap-2 sm:gap-3.5">
            <div className="flex items-center gap-2 h-9 pl-2.5 pr-3 rounded-full bg-[#1b1b1b] border border-[#2e2e2e] text-[13px] font-medium" data-testid="exe-conn-status" aria-label={user ? "Connected" : "Offline"}>
              <span className={`relative flex h-2 w-2`}>
                {user && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />}
                <span className={`relative inline-flex h-2 w-2 rounded-full ${user ? "bg-emerald-400" : "bg-neutral-500"}`} />
              </span>
              <span className={`hidden min-[480px]:inline ${user ? "text-emerald-300" : "text-neutral-400"}`}>{user ? "Connected" : "Offline"}</span>
            </div>
            <div className="relative">
              <button onClick={() => setUserMenu((v) => !v)} data-testid="exe-user-menu" className="flex items-center gap-2.5 h-9 pl-1 pr-2.5 rounded-full border border-[#2e2e2e] bg-[#1b1b1b] hover:bg-[#242424] transition-colors">
                <span className="h-7 w-7 rounded-full bg-gradient-to-br from-neutral-600 to-neutral-800 text-white text-xs font-bold flex items-center justify-center">{user ? (user.name || user.email).trim().charAt(0).toUpperCase() : "?"}</span>
                <span className="max-w-[80px] sm:max-w-[140px] truncate text-[13px] font-semibold text-neutral-200">{user ? (user.name || user.email).split("@")[0] : "Guest"}</span>
                <ChevronDown className={`h-4 w-4 text-neutral-500 transition-transform ${userMenu ? "rotate-180" : ""}`} />
              </button>
              {userMenu && (
                <div className="absolute right-0 mt-2 w-56 rounded-xl bg-[#191919] border border-[#2e2e2e] shadow-2xl shadow-black/60 p-2 text-sm" onMouseLeave={() => setUserMenu(false)}>
                  {user && <div className="px-3 py-2 text-[11px] text-neutral-500 truncate border-b border-[#2a2a2a] mb-1.5">{user.email}</div>}
                  <div className="flex gap-2 px-1 py-1"><LanguageSwitcher className="flex-1 justify-center" /></div>
                  <div className="px-1 py-1"><CheckUpdateButton /></div>
                  {user ? (
                    <button data-testid="exe-logout" onClick={() => { if (guardConfirm()) logout(); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-[#242424]"><LogOut className="h-4 w-4" /> {t("common.signOut")}</button>
                  ) : (
                    <button data-testid="exe-login" onClick={() => navigate("/login")} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-cyan-300 hover:bg-[#242424]">{t("common.loginRegister")}</button>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>
      )}
      <div className={`flex-1 min-h-0 flex ${IS_EXE ? "" : "contents"}`}>
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 ${IS_EXE ? "h-[calc(100vh-64px)] top-16 lg:h-full lg:top-0 shrink-0" : "h-screen"} w-[86vw] max-w-[320px] lg:w-64 ${IS_EXE ? "bg-[#121212] border-[#2a2a2a]" : "bg-[#0c1017] border-white/10"} border-r flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } ${collapsed ? "lg:hidden" : ""}`}
      >
        {!IS_EXE && (
        <div
          className="px-5 py-5 border-b border-white/10 flex items-center gap-2.5 cursor-pointer"
          onClick={() => { if (guardConfirm()) navigate(IS_EXE ? "/workspace" : IS_DESKTOP ? "/map-editor" : "/"); }}
          data-testid="sidebar-logo"
        >
          <img src={settings?.site_logo || "/trmd-logo.webp"} alt={settings?.brand_name || "TRMD Performance"} data-testid="sidebar-logo-img" className="h-10 w-auto max-w-[170px] object-contain" />
          <button
            data-testid="sidebar-collapse-btn"
            onClick={(e) => { e.stopPropagation(); setCollapsed(true); }}
            className="hidden lg:flex ml-auto h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            title="Collapse sidebar"
          >
            <PanelLeftClose className="h-5 w-5" />
          </button>
        </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain sidebar-scroll flex flex-col" style={{ WebkitOverflowScrolling: "touch" }}>
        <nav className={`px-3 py-3 ${IS_EXE ? "space-y-1 pt-4" : "space-y-0.5"}`}>
          {NAV.filter((item) => !item.adminOnly || isAdmin).map((item) => {
            const unlocked = isUnlocked(item);
            if (IS_EXE) return (
            <NavLink key={item.path} to={item.path} onClick={onNavClick} data-testid={`nav-${item.path.replace(/\//g, "")}`}
              className={({ isActive }) =>
                `relative flex items-center gap-3.5 pl-5 pr-3 py-3 rounded-lg text-[15px] font-medium transition-colors ${
                  isActive ? "bg-[#262626] text-white before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-r before:bg-[#e52321]" : "text-neutral-400 hover:text-white hover:bg-[#1f1f1f]"
                }`}>
              <item.icon className="h-5 w-5 shrink-0" />
              <span className="flex-1 truncate">{item.i18n ? t(item.i18n) : item.name}</span>
              {!unlocked && <Lock className="h-3 w-3 text-slate-600" />}
            </NavLink>
            );
            return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onNavClick}
              data-testid={`nav-${item.path.replace(/\//g, "")}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/30"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/60 border border-transparent"
                }`
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1">{item.i18n ? t(item.i18n) : item.name}</span>
              <span
                data-testid={`nav-lock-${item.path.replace(/\//g, "")}`}
                className={`inline-flex items-center gap-1 text-[9px] font-mono-tt font-bold uppercase tracking-wider px-1.5 py-0.5 rounded transition-colors ${
                  unlocked
                    ? "bg-white/5 text-slate-400 border border-white/10"
                    : "bg-slate-700/30 text-slate-500 border border-slate-600/30"
                }`}
              >
                {unlocked ? <Unlock className="h-2.5 w-2.5" /> : <Lock className="h-2.5 w-2.5" />}
                {unlocked ? t("lock.unlock") : t("lock.locked")}
              </span>
            </NavLink>
            );
          })}
          {isAdmin && !IS_EXE && (
            <NavLink
              to="/riwayat-login"
              onClick={onNavClick}
              data-testid="nav-riwayatlogin"
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/30"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/60 border border-transparent"
                }`
              }
            >
              <History className="h-4 w-4 shrink-0" />
              <span className="flex-1">Riwayat Login</span>
              <span className="text-[9px] font-mono-tt uppercase tracking-wider">2 Device</span>
            </NavLink>
          )}
          {isAdmin && !IS_EXE && (
            <NavLink
              to="/admin/desktop"
              onClick={onNavClick}
              data-testid="nav-admindesktop"
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                    : "text-emerald-500/70 hover:text-emerald-300 hover:bg-emerald-950/30 border border-transparent"
                }`
              }
            >
              <Download className="h-4 w-4 shrink-0" />
              <span className="flex-1">{t("nav.desktopApp")}</span>
              <span className="text-[9px] font-mono-tt uppercase tracking-wider">.EXE</span>
            </NavLink>
          )}
          {isAdmin && !IS_EXE && (
            <NavLink
              to="/admin/ecu-database"
              onClick={onNavClick}
              data-testid="nav-adminecudatabase"
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-sky-500/10 text-sky-400 border border-sky-500/30"
                    : "text-sky-500/70 hover:text-sky-300 hover:bg-sky-950/30 border border-transparent"
                }`
              }
            >
              <Database className="h-4 w-4 shrink-0" />
              <span className="flex-1">{t("nav.ecuDatabaseAdmin")}</span>
              <span className="text-[9px] font-mono-tt uppercase tracking-wider">Admin</span>
            </NavLink>
          )}
          {isAdmin && !IS_EXE && (
            <NavLink
              to="/admin/ecu-library"
              onClick={onNavClick}
              data-testid="nav-adminecolibrary"
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-sky-500/10 text-sky-400 border border-sky-500/30"
                    : "text-sky-500/70 hover:text-sky-300 hover:bg-sky-950/30 border border-transparent"
                }`
              }
            >
              <FileCode2 className="h-4 w-4 shrink-0" />
              <span className="flex-1">{t("nav.ecuLibrary")}</span>
              <span className="text-[9px] font-mono-tt uppercase tracking-wider">Admin</span>
            </NavLink>
          )}
          {isAdmin && !IS_EXE && (
            <NavLink
              to="/admin/users"
              onClick={onNavClick}
              data-testid="nav-adminusers"
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-sky-500/10 text-sky-400 border border-sky-500/30"
                    : "text-sky-500/70 hover:text-sky-300 hover:bg-sky-950/30 border border-transparent"
                }`
              }
            >
              <ShieldAlert className="h-4 w-4 shrink-0" />
              <span className="flex-1">{t("nav.userControl")}</span>
              {pendingCount > 0 ? (
                <span data-testid="pending-users-badge" className="min-w-[18px] h-[18px] px-1.5 flex items-center justify-center rounded-full bg-amber-500 text-slate-950 text-[10px] font-bold leading-none">{pendingCount}</span>
              ) : (
                <span className="text-[9px] font-mono-tt uppercase tracking-wider">Admin</span>
              )}
            </NavLink>
          )}
          {isAdmin && !IS_EXE && (
            <NavLink
              to="/admin/app-update"
              onClick={onNavClick}
              data-testid="nav-adminappupdate"
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-sky-500/10 text-sky-400 border border-sky-500/30"
                    : "text-sky-500/70 hover:text-sky-300 hover:bg-sky-950/30 border border-transparent"
                }`
              }
            >
              <Download className="h-4 w-4 shrink-0" />
              <span className="flex-1">{t("nav.appUpdate")}</span>
              <span className="text-[9px] font-mono-tt uppercase tracking-wider">APK/EXE</span>
            </NavLink>
          )}
          {isAdmin && !IS_EXE && (
            <NavLink
              to="/admin/brands"
              onClick={onNavClick}
              data-testid="nav-adminbrands"
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-sky-500/10 text-sky-400 border border-sky-500/30"
                    : "text-sky-500/70 hover:text-sky-300 hover:bg-sky-950/30 border border-transparent"
                }`
              }
            >
              <ImageIcon className="h-4 w-4 shrink-0" />
              <span className="flex-1">Brand &amp; Logo</span>
              <span className="text-[9px] font-mono-tt uppercase tracking-wider">Admin</span>
            </NavLink>
          )}
          {isAdmin && !IS_EXE && (
            <NavLink
              to="/admin/login-history"
              onClick={onNavClick}
              data-testid="nav-adminloginhistory"
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-sky-500/10 text-sky-400 border border-sky-500/30"
                    : "text-sky-500/70 hover:text-sky-300 hover:bg-sky-950/30 border border-transparent"
                }`
              }
            >
              <History className="h-4 w-4 shrink-0" />
              <span className="flex-1">Riwayat Login User</span>
              <span className="text-[9px] font-mono-tt uppercase tracking-wider">Admin</span>
            </NavLink>
          )}
        </nav>

          <div className="mt-auto">
        {IS_EXE && (
          <div className="px-4 py-4 border-t border-[#2a2a2a] flex items-center gap-3" data-testid="exe-sidebar-footer">
            <img src={settings?.site_logo || "/trmd-logo.webp"} alt="" className="h-9 w-auto max-w-[120px] object-contain shrink-0" />
            <div className="min-w-0 leading-tight">
              <div className="text-[10px] text-slate-500 truncate">Automotive ECU Solution</div>
            </div>
          </div>
        )}
        {!IS_DESKTOP && (
        <div className="p-3 border-t border-white/10">
          {user ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-2">
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-slate-700" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-slate-700" />
                )}
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-white truncate">{user.name || user.email}</div>
                  <div
                    data-testid="admin-status-indicator"
                    className={`text-[10px] font-mono-tt uppercase tracking-wider ${isAdmin ? "text-emerald-400" : "text-slate-500"}`}
                  >
                    {isAdmin ? t("common.superAdmin") : t("common.viewer")}
                  </div>
                </div>
              </div>
              <div className="flex gap-2"><LanguageSwitcher up className="flex-1 justify-center" /></div>
              <CheckUpdateButton />
              <Button
                data-testid="admin-logout-btn"
                onClick={() => { if (guardConfirm()) logout(); }}
                variant="ghost"
                className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800 h-9"
              >
                <LogOut className="h-4 w-4 mr-2" /> {t("common.signOut")}
              </Button>
            </div>
          ) : (
            <Button
              data-testid="admin-login-btn"
              onClick={() => navigate("/login")}
              className="w-full bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold"
            >
              {t("common.loginRegister")}
            </Button>
          )}
        </div>
        )}
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {collapsed && (
          <button
            data-testid="sidebar-open-btn"
            onClick={() => setCollapsed(false)}
            className="hidden lg:flex fixed top-3 left-3 z-30 h-9 w-9 items-center justify-center rounded-lg bg-[#0c1017] border border-white/10 text-slate-300 hover:text-white shadow-lg shadow-black/40 transition-colors"
            title="Show menu"
          >
            <PanelLeftOpen className="h-5 w-5" />
          </button>
        )}
        <header className="app-mobile-header lg:hidden sticky top-0 z-20 bg-[#0c1017]/95 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center gap-3">
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="text-slate-300">
            <ChevronLeft className="h-6 w-6 rotate-180" />
          </button>
          <span className="font-head font-black text-lg uppercase tracking-wide text-white">TRMD Performance</span>
          <div className="ml-auto flex items-center gap-2"><LanguageSwitcher showIcon={false} /></div>
        </header>
        <main className={`flex-1 overflow-x-hidden ${IS_EXE ? "min-h-0 overflow-y-auto overscroll-contain" : ""}`} data-testid="main-scroll">
          <LicenseBanner />
          <UpdateBanner />
          <Outlet context={{ registerGuard }} />
        </main>
      </div>
      </div>
    </div>
  );
}
